"use client";

/**
 * P3-08 · Buy-now button (flag-gated by `pdpBuyNow`, off until P4 checkout).
 *
 * Adds the resolved line then routes straight to checkout — the express path.
 * Success is silent (no "added" toast) because we navigate away immediately;
 * a server rejection still toasts and keeps the shopper on the PDP. Emits
 * `checkout.buy_now_started` on intent.
 */
import { useRouter } from "next/navigation";

import { CHECKOUT_PATH } from "~/config/storefront";
import type { CartPersonalisation } from "~/lib/cart/cart-interface";
import { cn } from "~/lib/utils";

import { useAddToCart } from "./use-add-to-cart";

export function BuyNowButton({
  variantId,
  quantity,
  personalisation,
  disabled,
  className,
}: {
  variantId: string;
  quantity: number;
  personalisation: CartPersonalisation;
  disabled: boolean;
  className?: string;
}) {
  const { add, isAdding } = useAddToCart({
    variantId,
    quantity,
    personalisation,
  });
  const router = useRouter();

  return (
    <button
      type="button"
      data-testid="pdp-buy-now"
      disabled={disabled || isAdding}
      aria-busy={isAdding}
      onClick={async () => {
        const ok = await add("buy_now", { silentSuccess: true });
        if (ok) router.push(CHECKOUT_PATH);
      }}
      className={cn(
        "w-full rounded-[var(--btn-radius)] border border-fg px-4 py-3 text-sm text-fg [font-weight:var(--btn-weight)] transition-colors hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
    >
      {isAdding ? "Processing…" : "Buy it now"}
    </button>
  );
}
